import { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => void;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}

export function EditableTitle({
  title,
  onSave,
  className,
  inputClassName,
  disabled = false,
}: EditableTitleProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setEditValue(title);
      setIsEditing(true);
    },
    [title],
  );

  const save = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== title) {
      onSave(trimmed);
    }
    setIsEditing(false);
  }, [editValue, title, onSave]);

  const cancel = useCallback(() => {
    setEditValue(title);
    setIsEditing(false);
  }, [title]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel],
  );

  if (isEditing) {
    return (
      <div
        className="flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={save}
          maxLength={100}
          className={cn(
            'h-7 text-sm px-2 py-1 bg-white/10 border border-white/20 rounded-md text-white outline-none focus:border-white/40',
            inputClassName,
          )}
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            save();
          }}
          className="p-1 rounded-md hover:bg-white/10 text-white/60 hover:text-white shrink-0"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className={cn('group/editable flex items-center gap-1', className)}>
      <span className="truncate cursor-default" title={title}>
        {title}
      </span>
      {!disabled && (
        <button
          type="button"
          onClick={startEditing}
          className="p-1 rounded-md hover:bg-white/10 text-white/40 hover:text-white/60 opacity-0 group-hover/editable:opacity-100 transition-opacity shrink-0"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
